import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronLeft, ChevronRight, Menu } from "lucide-react";
import styled from "styled-components";
import Panel from "@ui/panel/Panel";
import { GD } from "src/GD";
import { loadClientDirectory } from "src/services/clientDirectory";
import { useCurrentPath, useRouter } from "src/services/Router";
import { HoverTooltip } from "../../conponents/HoverTooltip";
import { Destination, TooltipContent, navigationTooltipStyle } from "../../popup/MerchantNavigationBar";
import type { NavigationClient } from "../../popup/merchantNavigation";
import { clearClientDashboardComparison, prepareClientDashboardComparison } from "../../statView/clientDashboardComparison";

const ClientRow = styled.button`
	display: flex; align-items: center; gap: 12px; width: 100%;
	text-align: left; padding: 12px; border: 0; border-bottom: 1px solid #edf0f4;
	border-radius: 6px; background: transparent; color: #182230; cursor: pointer;
	font: inherit; font-size: 14px;
	&:hover, &[aria-current="true"] { background: #edf4fb; }
	&:focus-visible { outline: 2px solid #168bdf; outline-offset: -2px; }
`;
const Search = styled.input`
	box-sizing: border-box; width: 100%; min-width: 0; padding: 10px 12px;
	border: 1px solid #ccd3dd; border-radius: 6px; font: inherit; font-size: 14px;
	&:focus { outline: 2px solid #168bdf; outline-offset: 1px; }
`;

export const ClientNavigation = () => {
	useLocale();
	const router = useRouter();
	const path = useCurrentPath();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const route = path ?? router;
	const clientId = Number(route.currentParams.cid);
	const comparisonScope = `${project?.id}:${user?.id}`;
	useEffect(() => clearClientDashboardComparison, [comparisonScope, route.currentNav?.id]);
	if (route.currentNav?.id !== "client" || !clientId || !project?.id || !user?.id) return null;
	return <ClientNavigationControls key={`${project.id}:${user.id}`} clientId={clientId} projectId={project.id}
		onNavigate={id => {
			prepareClientDashboardComparison(comparisonScope, clientId, id);
			router.navigate("client", { cid: String(id) });
		}} />;
};

export const ClientNavigationControls = ({ clientId, projectId, onNavigate }: {
	clientId: number; projectId: number; onNavigate: (id: number) => void;
}) => {
	useLocale();
	const [clients, setClients] = useState<NavigationClient[]>([]);
	const [busy, setBusy] = useState(true);
	const [error, setError] = useState(false);
	const [revision, setRevision] = useState(0);
	const [open, setOpen] = useState(false);
	useEffect(() => { setOpen(false); }, [clientId]);
	useEffect(() => {
		const subscription = GD.S_CLIENTS_UPDATE.subscribe(() => setRevision(value => value + 1));
		return () => { GD.S_CLIENTS_UPDATE.unsubscribe(subscription); };
	}, []);
	useEffect(() => {
		let cancelled = false;
		const controller = new AbortController();
		setBusy(true); setError(false); setClients([]);
		loadClientDirectory({}, { expectedProjectId: projectId, signal: controller.signal }).then(items => {
			const unique = new Map<number, NavigationClient>();
			for (const client of items) {
				if (Number.isSafeInteger(client?.id) && client.id > 0 && client.status === "active") unique.set(client.id, {
					id: client.id, name: client.name || msg("templateClient_1cf8a7e9", { v0: client.id }), status: client.status,
				});
			}
			if (!cancelled) setClients([...unique.values()].sort((a, b) => a.id - b.id));
		}).catch(() => { if (!cancelled) setError(true); })
			.finally(() => { if (!cancelled) setBusy(false); });
		return () => { cancelled = true; controller.abort(); };
	}, [projectId, revision]);
	const previous = clients.filter(client => client.id < clientId).at(-1);
	const next = clients.find(client => client.id > clientId);
	const navigate = (id: number) => { setOpen(false); if (id !== clientId) onNavigate(id); };
	const arrow = (direction: -1 | 1) => {
		const client = direction === -1 ? previous : next;
		const caption = direction === -1 ? msg("previousClient") : msg("nextClient");
		const label = busy ? msg("loadingClients") : error ? msg("couldNotLoadClients") : client ? `${caption}: ${client.name}` : msg("templateNo_76df8b38", { v0: caption.toLowerCase() });
		return <HoverTooltip key={`${direction}:${client?.id}`} align="end" tooltipStyle={navigationTooltipStyle}
			content={<TooltipContent>{client && !busy && !error ? <><div style={{ opacity: .75 }}>{caption}</div><strong>{client.name}</strong></> : label}</TooltipContent>}>
			<Destination type="button" disabled={busy || error || !client} aria-label={label} onClick={() => client && navigate(client.id)}>
				{direction === -1 ? <ChevronLeft size={19} /> : <ChevronRight size={19} />}
			</Destination>
		</HoverTooltip>;
	};
	return <div role="group" aria-label={msg("clientNavigation")} aria-busy={busy} style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: 8 }}>
		{arrow(-1)}
		<HoverTooltip align="end" tooltipStyle={navigationTooltipStyle} content={<TooltipContent>{msg("chooseClient")}</TooltipContent>}>
			<Destination type="button" aria-label={msg("chooseClient")} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}><Menu size={19} /></Destination>
		</HoverTooltip>
		{arrow(1)}
		{open && <ClientPicker clients={clients} clientId={clientId} busy={busy} error={error}
			onRetry={() => setRevision(value => value + 1)} onClose={() => setOpen(false)} onSelect={navigate} />}
	</div>;
};

const ClientPicker = ({ clients, clientId, busy, error, onRetry, onClose, onSelect }: {
	clients: NavigationClient[]; clientId: number; busy: boolean; error: boolean;
	onRetry: () => void; onClose: () => void; onSelect: (id: number) => void;
}) => {
	useLocale();
	const [search, setSearch] = useState("");
	const input = useRef<HTMLInputElement>(null);
	useEffect(() => {
		const previousFocus = document.activeElement;
		input.current?.focus({ preventScroll: true });
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true }); };
	}, []);
	const query = search.trim().toLowerCase();
	const filtered = clients.filter(client => `${client.name} ${client.id}`.toLowerCase().includes(query));
	return createPortal(<div role="dialog" aria-modal="true" aria-label={msg("chooseClient")}>
		<Panel decorator="absolute" label={msg("chooseClient")} onClose={onClose} growContent
			sx={{ width: "520px", maxWidth: "94vw", maxHeight: "80dvh", overflow: "hidden" }}>
			<div style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
				<Search ref={input} type="search" aria-label={msg("searchClients")} placeholder={msg("searchByNameOrId")} value={search} onChange={event => setSearch(event.target.value)} />
				{busy ? <div role="status">{msg("loadingClients")}</div> : error ? <div role="alert">{msg("couldNotLoadClients_9f14927d")}<button type="button" onClick={onRetry}>{msg("retry")}</button></div> : <>
					<div style={{ fontSize: 12, color: "#667085" }}>{query ? msg("templateOfClients_9c40e64c", { v0: filtered.length, v1: clients.length }) : msg("templateClients_318935b4", { v0: clients.length })}</div>
					<div style={{ overflow: "auto", minHeight: 0, maxHeight: "50dvh" }}>
						{filtered.map(client => <ClientRow key={client.id} type="button" aria-current={client.id === clientId ? "true" : undefined} onClick={() => onSelect(client.id)}>
							<div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, overflowWrap: "anywhere" }}>{client.name}</div>
								<div style={{ fontSize: 12, color: "#667085", marginTop: 4 }}>ID {client.id}{client.status ? ` · ${client.status}` : ""}</div></div>
							{client.id === clientId && <Check size={18} aria-label={msg("currentClient")} style={{ color: "#2563a6", flexShrink: 0 }} />}
						</ClientRow>)}
						{!filtered.length && <div role="status" style={{ padding: 12 }}>{msg("noClientsFound")}</div>}
					</div>
				</>}
			</div>
		</Panel>
	</div>, document.body);
};
